import {
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '../core/dialog';
import { cn } from '../../utils';

interface ZoomableImageProps {
  src: string;
  alt: string;
  title?: string;
  /** Classes for the inline (thumbnail) image. */
  className?: string;
  style?: CSSProperties;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'auto' | 'sync';
}

const ZOOM_FACTOR = 2;

/**
 * An image that opens full-size in a dialog when clicked. Inside the dialog a
 * second click (or the zoom button) doubles the image so small text becomes
 * readable, and the dialog scrolls to reach the rest of it.
 *
 * The trigger is the image itself (not a `<button>`) so it can safely sit inside
 * markdown that is already rendered within buttons or labels.
 */
export function ZoomableImage({
  src,
  alt,
  title,
  className,
  style,
  loading,
  decoding,
}: ZoomableImageProps) {
  const { t } = useTranslation('p2e');
  const [open, setOpen] = useState(false);
  const [zoomWidth, setZoomWidth] = useState<number | null>(null);
  const fullImageRef = useRef<HTMLImageElement>(null);
  const isZoomed = zoomWidth !== null;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setZoomWidth(null);
    }
  };

  const toggleZoom = (event?: SyntheticEvent) => {
    event?.stopPropagation();
    if (isZoomed) {
      setZoomWidth(null);
      return;
    }
    const width = fullImageRef.current?.getBoundingClientRect().width ?? 0;
    if (width > 0) {
      setZoomWidth(Math.round(width * ZOOM_FACTOR));
    }
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLImageElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  };

  const zoomLabel = isZoomed
    ? t('imageZoom.zoomOut', { defaultValue: 'Zoom out' })
    : t('imageZoom.zoomIn', { defaultValue: 'Zoom in' });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <img
          src={src}
          alt={alt}
          title={title}
          role="button"
          tabIndex={0}
          aria-label={t('imageZoom.open', { defaultValue: 'Enlarge image' })}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            'cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className,
          )}
          style={style}
          loading={loading}
          decoding={decoding}
        />
      </DialogTrigger>
      <DialogContent
        className="max-h-[95vh] max-w-[95vw] gap-2 overflow-auto border-none bg-black/95 p-2 text-white shadow-none"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <div className="flex items-center justify-between gap-3 pr-8 text-xs text-white/80">
          <span className="truncate">
            {t('imageZoom.hint', { defaultValue: 'Click the image to zoom in' })}
          </span>
          <button
            type="button"
            onClick={toggleZoom}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-white/30 px-2 py-1 text-xs text-white transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            {isZoomed ? <ZoomOut className="h-3.5 w-3.5" /> : <ZoomIn className="h-3.5 w-3.5" />}
            {zoomLabel}
          </button>
        </div>
        <div className={cn(isZoomed ? 'w-max' : 'flex items-center justify-center')}>
          <img
            ref={fullImageRef}
            src={src}
            alt={alt}
            onClick={toggleZoom}
            className={cn(
              'mx-auto object-contain',
              isZoomed ? 'max-w-none cursor-zoom-out' : 'max-h-[88vh] max-w-full cursor-zoom-in',
            )}
            style={isZoomed ? { width: zoomWidth } : undefined}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
